import { NavLink, useParams, useSearchParams } from 'react-router'

import { forgetNamedClasses } from '../annotation/namedClasses'
import { Icon } from '../components/Icon'
import { Markdown } from '../components/Markdown'
import { ErrorBox } from '../components/ui'
import { api, useAction, useApi } from '../lib/api'
import { useI18n, type MessageKey } from '../lib/i18n'
import { useAnchoredPopover } from '../lib/popover'
import { useSession } from '../lib/session'
import { logOut } from '../lib/sessionEnd'

interface LegalText {
  document: 'privacy' | 'imprint' | 'terms'
  text: string
  version: string | null
}

/** Privacy notice, imprint and terms of use: reachable from every page, and before login too (NTF-04). */
export const LEGAL_LINKS: readonly (readonly [string, MessageKey])[] = [
  ['/legal/privacy', 'privacyNotice'],
  ['/legal/imprint', 'imprint'],
  ['/legal/terms', 'termsOfUse'],
]

/** The legal texts from the navigation's foot, each in a new tab so the page stays as it is. */
export function LegalMenu({ collapsed }: { collapsed: boolean }) {
  const { t } = useI18n()
  const { panel, trigger, popover } = useAnchoredPopover<HTMLElement>()
  const label = t('legalTexts')
  return (
    <>
      <button {...trigger} type="button" className="nav-item" title={collapsed ? label : undefined}>
        <Icon name="legal" />
        <span className="nav-label">{label}</span>
      </button>
      <nav {...popover} className="popover-panel legal-menu" aria-label={label}>
        {LEGAL_LINKS.map(([to, key]) => (
          <a key={to} href={to} target="_blank" rel="noreferrer" onClick={() => panel.current?.hidePopover()}>
            {t(key)}
          </a>
        ))}
      </nav>
    </>
  )
}

/** One of the installation's legal texts; reachable without login, also from Keycloak's pages. */
export function LegalPage() {
  const { document = '' } = useParams()
  const [params] = useSearchParams()
  const { t, lang } = useI18n()
  const language = params.get('lang')?.startsWith('de') ? 'de' : params.get('lang')?.startsWith('en') ? 'en' : lang
  const legal = useApi<LegalText>(`/legal/${document}?lang=${language}`)
  return (
    <main className="container">
      <nav className="legal-nav" aria-label={t('legalTexts')}>
        {LEGAL_LINKS.map(([to, key]) => (
          <NavLink key={to} to={to}>
            {t(key)}
          </NavLink>
        ))}
      </nav>
      <ErrorBox error={legal.error} />
      {legal.data && <Markdown text={legal.data.text} />}
    </main>
  )
}

/**
 * The terms of use, accepted before anything else and again after every change (AUTH-10). Until then
 * the platform shows nothing else and asks the API nothing else, which refuses it too.
 */
export function TermsGate() {
  const { t, lang } = useI18n()
  const { reload } = useSession()
  const terms = useApi<LegalText>(`/legal/terms?lang=${lang}`)
  const { busy, error, run } = useAction()
  const accept = () =>
    run(async () => {
      await api('/me/terms', { json: { version: terms.data!.version } })
      reload()
    })
  return (
    <main className="container">
      <nav className="legal-nav" aria-label={t('legalTexts')}>
        {LEGAL_LINKS.filter(([to]) => to !== '/legal/terms').map(([to, key]) => (
          <a key={to} href={to} target="_blank" rel="noreferrer">
            {t(key)}
          </a>
        ))}
      </nav>
      <section className="card">
        <ErrorBox error={error ?? terms.error} />
        {terms.data && <Markdown text={terms.data.text} />}
        <div className="actions">
          <button type="button" disabled={busy || !terms.data} onClick={() => void accept()}>
            {t('acceptTermsOfUse')}
          </button>
          <button type="button" className="secondary" disabled={busy} onClick={() => {
              forgetNamedClasses() // they end with the session (LBL-08)
              void logOut()
            }}>
            {t('logout')}
          </button>
        </div>
      </section>
    </main>
  )
}
